<template>
    <div class="dashboard-container">

        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
                <h4 class="mb-0 fw-bold text-dark">Análise de Desempenho</h4>
                <span class="text-muted small">Métricas e indicadores para o período atual</span>
            </div>

            <div class="d-flex flex-wrap gap-2">

                <div class="position-relative custom-date-wrapper">
                    <button @click="openDateModal"
                        class="btn btn-light border shadow-sm btn-sm d-flex align-items-center text-muted fw-medium px-3 py-2 rounded-3 h-100">
                        <i class="bi bi-calendar3 me-2"></i> {{ formattedDateRange }}
                    </button>

                    <div v-if="showDatePicker"
                        class="custom-date-menu shadow-lg bg-white border rounded-4 p-3 position-absolute z-3">
                        <h6 class="fs-7 fw-bold mb-3 text-dark border-bottom pb-2">Selecionar Período</h6>

                        <div class="mb-2">
                            <label class="form-label fs-8 text-muted mb-1 fw-medium">Data Inicial</label>
                            <input type="date" v-model="tempStartDate" class="form-control form-control-sm text-muted">
                        </div>

                        <div class="mb-3">
                            <label class="form-label fs-8 text-muted mb-1 fw-medium">Data Final</label>
                            <input type="date" v-model="tempEndDate" class="form-control form-control-sm text-muted">
                        </div>

                        <div class="d-flex justify-content-end gap-2 mt-3">
                            <button @click="showDatePicker = false"
                                class="btn btn-sm btn-light border fs-8 fw-medium px-3 rounded-3">Cancelar</button>
                            <button @click="applyDateRange"
                                class="btn btn-sm btn-primary-pmg fs-8 fw-medium px-3 text-white border-0 rounded-3"
                                style="background-color: #561569;">Aplicar</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- <div class="alert alert-warning border-warning-subtle d-flex align-items-center shadow-sm rounded-3 mb-4">
            <i class="bi bi-exclamation-triangle-fill fs-5 text-warning me-3"></i>
            <div>
                <strong>Atenção:</strong> Seu limite mensal de uso do servidor está próximo de ser atingido.
            </div>
        </div> -->

        <div class="row g-3 mb-4">
            <div class="col-12 col-sm-6 col-xl-3" v-for="(metric, index) in metrics" :key="index">
                <div class="card card-metric border-0 shadow-sm rounded-4 h-100 p-3">
                    <div class="d-flex align-items-center mb-2">
                        <div class="icon-box rounded-3 me-2 d-flex align-items-center justify-content-center"
                            :class="metric.iconBg">
                            <i :class="metric.icon"></i>
                        </div>
                        <h6 class="text-muted mb-0 fw-medium fs-7">{{ metric.title }}</h6>
                    </div>
                    <h3 class="fw-bold mb-1 mt-2" :class="metric.valueColor || 'text-dark'">{{ metric.value }}</h3>
                    <div class="d-flex align-items-center small mt-1">
                        <span class="fw-bold me-1" :class="metric.trendColor">
                            <i :class="metric.trendIcon"></i> {{ metric.trendValue }}
                        </span>
                        <span class="text-muted fs-8">vs período anterior</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="row g-4 mb-4">
            <div class="col-xl-8 col-lg-12">
                <div class="card border-0 shadow-sm rounded-4 p-4 h-100">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 class="fw-bold text-dark mb-0">Entradas vs Saídas</h6>
                            <span class="text-success small fw-medium"><i class="bi bi-graph-up-arrow"></i> Tendência
                                Positiva</span>
                        </div>
                        <button class="btn btn-pmg-light text-muted border rounded-3"><i class="bi bi-arrow-repeat"></i>
                            Atualizar</button>
                    </div>
                    <div class="chart-wrapper">
                        <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100 text-muted">
                            <div class="spinner-border spinner-border-sm me-2" role="status"></div> Carregando...
                        </div>
                        <LineChartFinance v-else-if="chartsData.line" :api-data="chartsData.line" />
                    </div>
                </div>
            </div>

            <div class="col-xl-4 col-lg-12">
                <div class="card border-0 shadow-sm rounded-4 p-4 h-100">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h6 class="fw-bold text-dark mb-0"><i class="bi bi-receipt me-2 text-muted"></i>Transações
                            Recentes</h6>
                        <span class="text-muted small">7 dias</span>
                    </div>
                    <div class="transaction-list flex-grow-1">
                        <div class="d-flex align-items-center mb-3 pb-3 border-bottom"
                            v-for="item in recentTransactions" :key="item.id">
                            <div class="avatar-circle fw-bold small me-3" :class="item.avatarClass">
                                {{ item.initials }}
                            </div>
                            <div class="flex-grow-1">
                                <h6 class="mb-0 fs-7 fw-bold text-dark">{{ item.client }}</h6>
                                <span class="text-muted fs-8">{{ item.description }}</span>
                            </div>
                            <div class="text-end">
                                <h6 class="mb-0 fs-7 fw-bold text-dark">{{ item.value }}</h6>
                                <span class="badge rounded-pill fw-medium"
                                    :class="'bg-' + item.statusColor + '-subtle text-' + item.statusColor">
                                    {{ getStatusLabel(item.status) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <router-link class="btn btn-pmg-light border w-100 text-muted fw-medium mt-2 fs-7 rounded-3"
                        to="financial">Ver todas</router-link>
                </div>
            </div>
        </div>

        <div class="row g-4">
            <div class="col-xl-8 col-lg-12">
                <div class="card border-0 shadow-sm rounded-4 p-4 h-100">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="fw-bold text-dark mb-0">Receitas vs Despesas (Ano)</h6>
                        <button class="btn btn-sm btn-light text-muted rounded-3"><i
                                class="bi bi-three-dots"></i></button>
                    </div>
                    <div class="chart-wrapper">
                        <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100 text-muted">
                            <div class="spinner-border spinner-border-sm me-2" role="status"></div> Carregando...
                        </div>
                        <BarChartFinance v-else-if="chartsData.bar" :api-data="chartsData.bar" />
                    </div>
                </div>
            </div>

            <div class="col-xl-4 col-lg-12">
                <div class="card border-0 shadow-sm rounded-4 p-4 h-100">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h6 class="fw-bold text-dark mb-0"><i class="bi bi-credit-card me-2 text-muted"></i>Meios de Pagamento (Receitas)</h6>
                    </div>
                    <div class="d-flex flex-column gap-3">
                        <div v-for="method in paymentMethods" :key="method.name">
                            <div class="d-flex justify-content-between align-items-end mb-1">
                                <span class="fs-7 fw-medium text-dark">{{ translateMethodPayment(method.name) }}</span>
                                <span class="fs-7 text-muted">{{ method.percentage }}%</span>
                            </div>
                            <div class="progress" style="height: 6px;">
                                <div class="progress-bar" :class="method.colorClass" role="progressbar"
                                    :style="{ width: method.percentage + '%' }" aria-valuenow="25" aria-valuemin="0"
                                    aria-valuemax="100"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="modal fade" id="modalDateFilter" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4">
                <div class="modal-header border-0 pt-4 px-4 pb-2">
                    <h5 class="fw-bold m-0 text-dark">
                        <i class="bi bi-calendar-range me-2 text-primary"></i> Filtrar Período
                    </h5>
                    <button type="button" class="btn-close" @click="closeDateModal"></button>
                </div>
                <div class="modal-body p-4">
                    <p class="text-muted small mb-4">Selecione o intervalo de datas para atualizar os indicadores do
                        painel.</p>

                    <div class="row g-3">
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Data Inicial</label>
                            <input type="date" v-model="tempStartDate" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Data Final</label>
                            <input type="date" v-model="tempEndDate" class="form-control form-control-pmg">
                        </div>
                    </div>

                    <div class="d-flex justify-content-end gap-2 mt-5">
                        <button type="button" class="btn btn-pmg-light px-4" @click="closeDateModal">Cancelar</button>
                        <button type="button" class="btn btn-pmg-primary px-5" @click="applyDateRange">Aplicar
                            Filtro</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import BarChartFinance from './BarChartFinance.vue';
import LineChartFinance from './LineChartFinance.vue';

export default {
    name: 'DashboardIndex',
    components: { LineChartFinance, BarChartFinance },
    data() {
        const defaultDates = this.getInitialDateRange();
        return {
            isLoading: true,
            showDatePicker: false,
            startDate: defaultDates.start,
            endDate: defaultDates.end,
            tempStartDate: '',
            tempEndDate: '',

            // 1. MANTEMOS A ESTRUTURA PARA O LAYOUT NÃO SUMIR ANTES DA API RESPONDER
            metrics: [
                { title: 'Total de Receitas', value: 'Carregando...', valueColor: 'text-success', icon: 'bi-graph-up-arrow', iconBg: 'bg-success-subtle text-success', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Faturamento Líquido', value: 'Carregando...', icon: 'bi-wallet2', iconBg: 'bg-info-subtle text-info', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Total de Vendas', value: '...', icon: 'bi-cart-check', iconBg: 'bg-success-subtle text-success', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Ticket Médio', value: 'Carregando...', icon: 'bi-tag', iconBg: 'bg-warning-subtle text-warning', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Saldo em Contas', value: 'Carregando...', icon: 'bi-bank', iconBg: 'bg-secondary-subtle text-secondary', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Total de Despesas', value: 'Carregando...', valueColor: 'text-danger', icon: 'bi-graph-down-arrow', iconBg: 'bg-danger-subtle text-danger', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Clientes Ativos', value: '...', icon: 'bi-people', iconBg: 'bg-primary-subtle text-primary', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' },
                { title: 'Inadimplência', value: '...', icon: 'bi-shield-exclamation', iconBg: 'bg-danger-subtle text-danger', trendValue: '-', trendIcon: 'bi-dash', trendColor: 'text-muted' }
            ],

            // 2. MANTEMOS OS ARRAYS DE APOIO
            recentTransactions: [],
            paymentMethods: [],
            chartsData: { line: null, bar: null }
        }
    },
    computed: {
        formattedDateRange() {
            if (!this.startDate || !this.endDate) return 'Selecione o período';
            return `${this.formatDateStr(this.startDate)} - ${this.formatDateStr(this.endDate)}`;
        }
    },
    mounted() {
        this.fetchDashboardData();
    },
    methods: {
        async fetchDashboardData() {
            this.isLoading = true;
            try {
                // Rota da sua API
                const response = await api.get('/dashboard', {
                    params: {
                        start_date: this.startDate,
                        end_date: this.endDate
                    }
                });

                const payload = response.data.data || response.data;

                // 3. A API RESPONDEU? A MÁGICA ACONTECE AQUI SUBSTITUINDO OS DADOS "CARREGANDO..."
                if (payload.metrics) this.metrics = payload.metrics;
                if (payload.recentTransactions) this.recentTransactions = payload.recentTransactions;
                if (payload.paymentMethods) this.paymentMethods = payload.paymentMethods;
                if (payload.chartData) this.chartsData = payload.chartData;

            } catch (error) {
                console.error("Erro ao carregar o dashboard:", error);
            } finally {
                this.isLoading = false;
            }
        },

        toggleDatePicker() {
            this.tempStartDate = this.startDate;
            this.tempEndDate = this.endDate;
            this.showDatePicker = !this.showDatePicker;
        },

      
        formatDateStr(dateStr) {
            if (!dateStr) return '';
            const [year, month, day] = dateStr.split('-');
            const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
            const monthName = monthNames[parseInt(month) - 1];
            return `${day} ${monthName} ${year}`;
        },

        getInitialDateRange() {
    const year = new Date().getFullYear();

    return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
    };
},

        // Agora você pode ter um método de "Reset" simples
        resetToCurrentYear() {
            const dates = this.getInitialDateRange();
            this.startDate = dates.start;
            this.endDate = dates.end;
            this.fetchDashboardData();
        },

        getStatusLabel(s) { return { Pending: 'Pendente', Paid: 'Pago', Overdue: 'Atrasado', Canceled: 'Cancelado', Partial: 'Parcial' }[s] || s; },

        openDateModal() {
            this.tempStartDate = this.startDate;
            this.tempEndDate = this.endDate;
            const modal = window.bootstrap.Modal.getOrCreateInstance(document.getElementById('modalDateFilter'));
            modal.show();
        },
        
        closeDateModal() {
            const modal = window.bootstrap.Modal.getInstance(document.getElementById('modalDateFilter'));
            if (modal) modal.hide();
        },
        
        applyDateRange() {
            this.startDate = this.tempStartDate;
            this.endDate = this.tempEndDate;
            this.closeDateModal(); 
            this.fetchDashboardData();
        },

        translateMethodPayment(status) {
            const map = {
                'BOLETO':'Boleto',
                'PIX':'Pix',
                'CREDIT_CARD': 'Cartão de crédito',
            };
            return map[status] || status;
        },
    }
}
</script>

<style scoped>
/* Tipografia e Cores Base */
.fs-7 {
    font-size: 0.85rem;
}

.fs-8 {
    font-size: 0.75rem;
}

.text-dark {
    color: #1e293b !important;
}

.text-muted {
    color: #64748b !important;
}

/* -------------------------------------------------------------
   Estilos do Novo Calendário Customizado
-------------------------------------------------------------- */
.custom-date-menu {
    top: calc(100% + 8px);
    /* Fica grudado logo abaixo do botão */
    left: 0;
    min-width: 280px;
    animation: fadeInDown 0.2s ease-out;
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* -------------------------------------------------------------
   A MÁGICA DOS GRÁFICOS
-------------------------------------------------------------- */
.chart-wrapper {
    position: relative;
    height: 320px;
    width: 100%;
    margin-top: 10px;
}

/* Cartões de Métrica */
.card-metric {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card-metric:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
}

.icon-box {
    width: 38px;
    height: 38px;
    font-size: 1.1rem;
}

/* Transações Recentes */
.avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.transaction-list>div:last-child {
    border-bottom: none !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
}

/* Utilitários de Cor Suaves */
.bg-primary-subtle {
    background-color: #eff6ff !important;
}

.text-primary {
    color: #2563eb !important;
}

.bg-success-subtle {
    background-color: #f0fdf4 !important;
}

.text-success {
    color: #16a34a !important;
}

.bg-warning-subtle {
    background-color: #fefce8 !important;
}

.text-warning {
    color: #eab308 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
}

.text-danger {
    color: #dc2626 !important;
}

.bg-info-subtle {
    background-color: #ecfeff !important;
}

.text-info {
    color: #0891b2 !important;
}

.bg-secondary-subtle {
    background-color: #f8fafc !important;
}

.text-secondary {
    color: #475569 !important;
}

.z-3 {
    z-index: 3;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    padding: 10px 20px;
}

/* Adicione estas classes no <style scoped> do dashboard se ainda não estiverem aí */
.form-control-pmg {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 14px;
}
.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 3px rgba(33, 51, 97, 0.1);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border-radius: 12px !important;
    padding: 10px 24px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-pmg-light {
    border-radius: 12px !important;
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 500;
}
</style>